/**
 * @file SettingsView.tsx
 *
 * Settings tab screen. Loads the shop settings (business fields + the shopfront
 * `locale`) and the localized country list, seeds {@link SettingsForm}, and on save
 * persists the shop-settings patch (locale included) through the `/v1` shop-settings
 * API. The outcome is surfaced as a toast plus an inline error. Reached via the
 * `/settings` nav tab. Mirrors WSB-v2's Shop settings, re-homed under our existing tab.
 */

import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Loader } from '@group.one/react-gravity';
import { adminMessages } from '../i18n/messages';
import { fetchShopSettings, updateShopSettings } from '../api/shop-settings';
import type { ShopSettings } from '../api/shop-types';
import { loadContinents } from '../shipping/region-data';
import { localizeContinents } from '../shipping/region-localization';
import { getCountryOptions } from '../shipping/shipping-region-utils';
import { notifyError, notifySuccess } from '../feedback/toast';
import { EmptyState } from '../components/EmptyState';
import { SettingsForm } from './SettingsForm';
import { buildSettingsPatch, deriveSettingsFields } from './settings-form-utils';
import type { SettingsFormFields, SettingsOption } from './settings-form-types';

/**
 * Renders the Settings tab.
 *
 * @returns The settings screen.
 */
export function SettingsView() {
  const intl = useIntl();
  const [settings, setSettings] = useState<ShopSettings>();
  const [countryOptions, setCountryOptions] = useState<SettingsOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [reloadNonce, setReloadNonce] = useState(0);

  // Seed: the shop settings (business fields + shopfront locale), loaded on mount and on retry.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    void fetchShopSettings()
      .then((loaded) => {
        if (active) setSettings(loaded);
      })
      .catch(() => {
        // Surface the failure rather than seeding the form from `undefined`: an empty seed
        // is indistinguishable from a new shop, and saving from it would overwrite the
        // stored email/locale/address with blanks (see buildSettingsPatch → toGeneralPatch).
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reloadNonce]);

  // Country options: localized per the active admin locale.
  useEffect(() => {
    let active = true;
    void loadContinents()
      .then((continents) => {
        if (active) {
          setCountryOptions(getCountryOptions(localizeContinents(continents, intl.locale)));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [intl.locale]);

  const seed = useMemo(() => deriveSettingsFields(settings), [settings]);

  const handleSubmit = async (fields: SettingsFormFields): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    try {
      const saved = await updateShopSettings(buildSettingsPatch(fields));
      setSettings(saved);
      notifySuccess(intl.formatMessage(adminMessages.toastSaved));
    } catch {
      setSaveError(intl.formatMessage(adminMessages.toastError));
      notifyError(intl.formatMessage(adminMessages.toastError));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gv-flex gv-justify-center gv-p-lg">
        <Loader condensed />
      </div>
    );
  }

  if (loadError) {
    return (
      <EmptyState
        title={intl.formatMessage(adminMessages.settingsLoadErrorTitle)}
        description={intl.formatMessage(adminMessages.settingsLoadErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.settingsLoadRetry),
          onClick: () => setReloadNonce((nonce) => nonce + 1),
        }}
        testId="settings-load-error"
      />
    );
  }

  return (
    <SettingsForm
      seed={seed}
      countryOptions={countryOptions}
      saving={saving}
      saveError={saveError}
      onSubmit={handleSubmit}
    />
  );
}
